import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { categoryLabel } from "../lib/categories";
import PaymentModal from "../components/PaymentModal";
import InquiryModal from "../components/InquiryModal";
import WishlistButton from "../components/WishlistButton";
import RelatedProducts from "../components/RelatedProducts";
import SEOHead from "../components/SEOHead";
import { useCart } from "../lib/cart";
import { useSettings } from "../lib/settings";
import { ShoppingBag, Sparkles, Tag, Download, ExternalLink, Plus, Play, Mic, MessageCircle } from "lucide-react";
import { toast } from "sonner";

// Best-effort hex map for common Printful color names. Falls back to a neutral chip with label.
const COLOR_SWATCH = {
  black: "#1a1a1a", white: "#fafafa", "off-white": "#f5f3ee", cream: "#f0e6d2", ivory: "#f6f0dc",
  beige: "#d8c9ad", tan: "#c9a87c", sand: "#cfb992", khaki: "#bfae8a", brown: "#5c3a1a",
  navy: "#1c2a4a", "navy blue": "#1c2a4a", blue: "#2a5db0", "light blue": "#a3c8e8", "royal blue": "#1d3fa9",
  red: "#c1272d", "true red": "#c0182b", maroon: "#6e1322", burgundy: "#7c1d3d", pink: "#f5b3c1",
  "light pink": "#fbd6dc", "hot pink": "#e63a87", "soft pink": "#f7c8d3",
  green: "#3a7d44", olive: "#6b6b1f", forest: "#264d2e", mint: "#b9e3c6", "kelly green": "#1f7a3a",
  yellow: "#f0c419", gold: "#d4af37", mustard: "#c48b18",
  orange: "#e88234", coral: "#f08080",
  purple: "#5e2a8b", lavender: "#cbb3e0", "heather purple": "#8a7da6",
  grey: "#8c8c8c", gray: "#8c8c8c", "heather grey": "#a8a39a", "heather gray": "#a8a39a", charcoal: "#3b3b3b", "dark grey": "#4a4a4a",
  silver: "#c9c9c9",
};

function colorBg(name) {
  if (!name) return "#e6e0d0";
  return COLOR_SWATCH[name.toLowerCase()] || "#e6e0d0";
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({}); // { [groupName]: value }
  const cart = useCart();
  const { settings } = useSettings();

  // Normalize variants: ensure each variant has an `options` dict (back-compat from color/size)
  const normalizedVariants = useMemo(() => {
    const vs = product?.variants || [];
    return vs.map((v) => {
      const opts = { ...(v.options || {}) };
      if (!opts.Color && v.color) opts.Color = v.color;
      if (!opts.Size && v.size) opts.Size = v.size;
      return { ...v, options: opts };
    });
  }, [product]);

  // Derive option groups: from product.option_groups OR auto-build from variant options
  const optionGroups = useMemo(() => {
    if (product?.option_groups?.length) return product.option_groups;
    const map = new Map();
    normalizedVariants.forEach((v) => {
      Object.entries(v.options || {}).forEach(([name, value]) => {
        if (!map.has(name)) map.set(name, { name, kind: name.toLowerCase() === "color" ? "color" : "text", values: [], _seen: new Set() });
        const g = map.get(name);
        if (value && !g._seen.has(value)) { g._seen.add(value); g.values.push({ value }); }
      });
    });
    return Array.from(map.values()).map(({ _seen, ...g }) => g);
  }, [product, normalizedVariants]);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setActiveImage(0);
      setSelectedOptions({});
    }).catch(() => setProduct(false)).finally(() => setLoading(false));
  }, [id]);

  // Auto-pick the first valid variant's options on load (so labels populate immediately)
  useEffect(() => {
    if (!optionGroups.length || !normalizedVariants.length) return;
    if (Object.keys(selectedOptions).length) return;
    const firstWithOptions = normalizedVariants.find((v) => v.options && Object.keys(v.options).length);
    if (firstWithOptions) {
      setSelectedOptions({ ...firstWithOptions.options });
      return;
    }
    const init = {};
    optionGroups.forEach((g) => {
      if (g.kind === "engraving") return;
      const firstVal = g.values?.[0]?.value;
      if (firstVal) init[g.name] = firstVal;
    });
    setSelectedOptions(init);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionGroups, normalizedVariants]);

  const hasVariants = normalizedVariants.length > 0;
  const variantGroups = useMemo(() => optionGroups.filter((g) => g.kind !== "engraving"), [optionGroups]);
  const engravingGroup = useMemo(() => optionGroups.find((g) => g.kind === "engraving") || null, [optionGroups]);

  // Which option values are still valid given current partial selection?
  const isValueAvailable = (groupName, value) => {
    const draft = { ...selectedOptions, [groupName]: value };
    return normalizedVariants.some((v) =>
      variantGroups.every((g) => (v.options || {})[g.name] === draft[g.name])
    );
  };

  // Tiffany-style "snap" behavior: pick a value; if it makes the combo invalid,
  // jump to the closest valid variant that contains the picked value.
  const pickOption = (groupName, value) => {
    const draft = { ...selectedOptions, [groupName]: value };
    const exact = normalizedVariants.find((v) =>
      variantGroups.every((g) => (v.options || {})[g.name] === draft[g.name])
    );
    if (exact) { setSelectedOptions(draft); return; }
    let best = null; let bestScore = -1;
    normalizedVariants.forEach((v) => {
      if ((v.options || {})[groupName] !== value) return;
      const score = Object.entries(selectedOptions).reduce((s, [k, val]) =>
        s + ((v.options || {})[k] === val ? 1 : 0), 0);
      if (score > bestScore) { best = v; bestScore = score; }
    });
    if (best) setSelectedOptions({ ...best.options, ...(selectedOptions[engravingGroup?.name] ? { [engravingGroup.name]: selectedOptions[engravingGroup.name] } : {}) });
    else setSelectedOptions(draft);
  };

  const selectedVariant = useMemo(() => {
    if (!hasVariants) return null;
    return normalizedVariants.find((v) =>
      variantGroups.every((g) => (v.options || {})[g.name] === selectedOptions[g.name])
    ) || null;
  }, [hasVariants, normalizedVariants, variantGroups, selectedOptions]);

  // If variant has an image_url, surface it as the cover image
  const gallery = useMemo(() => {
    if (!product) return [];
    const list = [];
    if (selectedVariant?.image_url) list.push(selectedVariant.image_url);
    if (product.image_url && !list.includes(product.image_url)) list.push(product.image_url);
    (product.images || []).forEach((src) => { if (src && !list.includes(src)) list.push(src); });
    return list;
  }, [product, selectedVariant]);

  // Reset gallery cursor when variant image changes
  useEffect(() => { setActiveImage(0); }, [selectedVariant?.image_url]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-[#5C5547]">Loading…</div>;
  if (!product) return (
    <div className="max-w-7xl mx-auto px-4 py-24 text-center" data-testid="product-not-found">
      <div className="font-serif text-3xl">Product not found.</div>
      <Link to="/shop" className="mt-4 inline-block text-sm text-[var(--accent)]">← Back to shop</Link>
    </div>
  );

  const isAffiliate = product.category === "affiliate" && product.affiliate_url;
  const isExternal = !!(product.external_checkout_url || "").trim();
  const isMedia = product.category === "media";
  const isFree = product.price === 0;
  const isPhysical = !product.is_digital;

  return (
    <div data-testid="product-detail" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
      <SEOHead
        title={`${product.title}${product.price ? ` — $${Number(product.price).toFixed(2)}` : ""} · This That For Me`}
        description={(product.description || "").slice(0, 200) || product.title}
        image={product.image_url}
      />
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          {isMedia && product.video_url ? (
            <div className="aspect-video rounded-3xl overflow-hidden border-2 border-[var(--accent)]/30 bg-black" data-testid="product-video-embed">
              <iframe src={product.video_url} title={product.title}
                className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          ) : isMedia && product.podcast_url ? (
            <div className="rounded-3xl overflow-hidden border-2 border-[var(--accent)]/30 bg-[#F5F2EA] p-2" data-testid="product-podcast-embed">
              <iframe src={product.podcast_url} title={product.title}
                className="w-full h-[352px] rounded-2xl" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-[#F5F2EA] border-2 border-[var(--accent)]/30">
                {gallery[activeImage] && (
                  <img src={gallery[activeImage]} alt={product.title} className="w-full h-full object-cover transition-opacity" />
                )}
              </div>
              {gallery.length > 1 && (
                <div className="grid grid-cols-5 gap-2" data-testid="product-thumbnails">
                  {gallery.slice(0, 10).map((src, i) => (
                    <button key={src + i} type="button"
                      onClick={() => setActiveImage(i)}
                      data-testid={`product-thumb-${i}`}
                      className={`aspect-square rounded-xl overflow-hidden border-2 transition ${i === activeImage ? "border-[var(--accent)]" : "border-[#EAE4D3] hover:border-[var(--accent)]/50"}`}>
                      <img src={src} alt={`${product.title} ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="md:col-span-5 md:sticky md:top-28 self-start">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#F5F2EA] text-[10px] uppercase tracking-[0.2em]">
              <Tag className="w-3 h-3" /> {categoryLabel(product.category)}
            </span>
            {product.platform && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#2C2924] text-[#F3E5AB] text-[10px] uppercase tracking-[0.2em]">
                <Sparkles className="w-3 h-3" /> {product.platform}
              </span>
            )}
            {isMedia && product.video_url && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-50 text-red-700 text-[10px] uppercase tracking-[0.2em]"><Play className="w-3 h-3" /> Video</span>
            )}
            {isMedia && product.podcast_url && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-50 text-green-700 text-[10px] uppercase tracking-[0.2em]"><Mic className="w-3 h-3" /> Podcast</span>
            )}
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight">{product.title}</h1>
          {(() => {
            const displayPrice = selectedVariant?.price ?? product.price;
            return displayPrice > 0 ? (
              <div className="mt-4 font-serif text-3xl gold-text" data-testid="product-price">${Number(displayPrice).toFixed(2)}</div>
            ) : null;
          })()}
          {/* Stock badge — Made to order / Only X left / Sold out */}
          {selectedVariant && (() => {
            const stk = selectedVariant.stock;
            if (stk === null || stk === undefined) {
              return <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F5F2EA] text-[10px] uppercase tracking-[0.2em] text-[#5C5547]" data-testid="stock-badge">Made to order</div>;
            }
            if (stk === 0) {
              return <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-[10px] uppercase tracking-[0.2em] text-red-700" data-testid="stock-badge">Sold out</div>;
            }
            if (stk <= 3) {
              return <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--accent)]/15 text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]" data-testid="stock-badge">Only {stk} left</div>;
            }
            return <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 text-[10px] uppercase tracking-[0.2em] text-green-700" data-testid="stock-badge">In stock</div>;
          })()}
          {isFree && isMedia && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[var(--accent)]/15 text-[var(--accent)] text-sm font-medium">
              Free to watch / listen
            </div>
          )}
          <p className="mt-6 text-[#5C5547] leading-relaxed">{product.description}</p>

          {hasVariants && optionGroups.length > 0 && (
            <div className="mt-8 space-y-5" data-testid="variant-selector">
              {optionGroups.map((g) => {
                const isColor = g.kind === "color" || g.name.toLowerCase() === "color";
                const isEngraving = g.kind === "engraving";
                if (isEngraving) {
                  const val = selectedOptions[g.name] || "";
                  return (
                    <div key={g.name}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547]">{g.name} <span className="text-[#5C5547]/70">(optional)</span></div>
                        <div className="text-xs text-[#5C5547]" data-testid={`engraving-count-${g.name.toLowerCase()}`}>{val.length}/30</div>
                      </div>
                      <input
                        value={val}
                        onChange={(e) => setSelectedOptions((p) => ({ ...p, [g.name]: e.target.value.slice(0, 30) }))}
                        placeholder="Initials, name, or short message"
                        maxLength={30}
                        data-testid={`engraving-input-${g.name.toLowerCase()}`}
                        className="w-full px-4 py-3 rounded-xl bg-[#FDFBF7] border border-[#EAE4D3] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm font-serif italic"
                      />
                    </div>
                  );
                }
                return (
                  <div key={g.name}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547]">{g.name}</div>
                      <div className="text-sm text-[#2C2924]" data-testid={`selected-${g.name.toLowerCase()}-label`}>
                        {selectedOptions[g.name] || "—"}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2.5">
                      {g.values.map((opt) => {
                        const val = typeof opt === "string" ? opt : opt.value;
                        const hex = typeof opt === "object" ? opt.hex : null;
                        const active = selectedOptions[g.name] === val;
                        const available = isValueAvailable(g.name, val);
                        const testId = `${isColor ? "color-swatch" : g.name.toLowerCase().replace(/\s+/g, "-")}-${val.toLowerCase().replace(/\s+/g, "-")}`;
                        if (isColor) {
                          return (
                            <button key={val} type="button"
                              onClick={() => pickOption(g.name, val)}
                              aria-label={val}
                              data-testid={testId}
                              title={val}
                              className={`w-9 h-9 rounded-full border-2 transition ${active ? "border-[var(--accent)] ring-2 ring-[var(--accent)]/30" : "border-[#EAE4D3] hover:border-[var(--accent)]/60"} ${available ? "" : "opacity-60"}`}
                              style={{ backgroundColor: hex || colorBg(val) }}
                            />
                          );
                        }
                        return (
                          <button key={val} type="button"
                            onClick={() => pickOption(g.name, val)}
                            data-testid={testId}
                            className={`min-w-[3rem] px-4 py-2 rounded-full border text-sm transition ${active ? "bg-[#2C2924] text-[#FDFBF7] border-[#2C2924]" : "bg-[#FDFBF7] text-[#2C2924] border-[#EAE4D3] hover:border-[var(--accent)]"} ${available ? "" : "opacity-60"}`}>
                            {val}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8 space-y-3">
            {isAffiliate ? (
              <a href={product.affiliate_url} target="_blank" rel="noreferrer"
                data-testid="affiliate-link-btn"
                className="w-full flex items-center justify-center gap-2 py-4 rounded-full bg-[var(--accent)] text-white hover:opacity-90 text-sm font-medium">
                Open affiliate link <ExternalLink className="w-4 h-4" />
              </a>
            ) : isExternal ? (
              <div className="flex gap-2 items-stretch">
                <a href={product.external_checkout_url} data-testid="external-checkout-button"
                  className="flex-1 flex items-center justify-center gap-2 py-4 rounded-full bg-[#2C2924] text-[#FDFBF7] hover:bg-[#1A1815] text-sm font-medium">
                  <ShoppingBag className="w-4 h-4" /> Buy now — ${Number(product.price).toFixed(2)}
                </a>
                <WishlistButton product={product} variant={null} />
              </div>
            ) : isFree ? (
              <div className="text-sm text-[#5C5547]">Enjoy — no purchase needed.</div>
            ) : (() => {
              const needsVariant = hasVariants && !selectedVariant;
              const soldOut = selectedVariant && selectedVariant.stock === 0;
              const blocked = needsVariant || soldOut;
              const displayPrice = selectedVariant?.price ?? product.price;
              const showInquiry = displayPrice >= 1000;
              const btnText = soldOut ? "Sold out"
                : needsVariant ? "Select options first"
                : `Buy now — $${Number(displayPrice).toFixed(2)}`;
              return (
                <>
                  <div className="flex gap-2 items-stretch">
                    <button onClick={() => setOpen(true)} disabled={blocked} data-testid="buy-now-button"
                      className="flex-1 flex items-center justify-center gap-2 py-4 rounded-full bg-[#2C2924] text-[#FDFBF7] hover:bg-[#1A1815] text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                      <ShoppingBag className="w-4 h-4" /> {btnText}
                    </button>
                    <WishlistButton product={product} variant={selectedVariant} />
                  </div>
                  <button onClick={() => {
                    if (blocked) { toast.error(soldOut ? "This option is sold out." : "Please pick a size/color first."); return; }
                    cart.add(product, selectedVariant);
                    toast.success("Added to bag.");
                  }} disabled={blocked} data-testid="add-to-cart-button"
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full border-2 border-[var(--accent)]/40 text-[#2C2924] hover:bg-[var(--accent)]/10 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                    <Plus className="w-4 h-4" /> Add to bag
                  </button>
                  {showInquiry && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <button onClick={() => setInquiryOpen(true)} data-testid="request-viewing-button"
                        className="flex items-center justify-center gap-2 py-3 rounded-full bg-[var(--accent)]/10 text-[#2C2924] hover:bg-[var(--accent)]/20 text-xs font-medium tracking-[0.05em] uppercase">
                        <Sparkles className="w-3.5 h-3.5 text-[var(--accent)]" /> Private viewing
                      </button>
                      {settings?.whatsapp_number && (() => {
                        const msg = encodeURIComponent(
                          `Hi! I'm interested in ${product.title}${selectedVariant?.name ? ` (${selectedVariant.name})` : ""} on your site. Could we chat?`
                        );
                        const href = `https://wa.me/${settings.whatsapp_number.replace(/\D/g, "")}?text=${msg}`;
                        return (
                          <a href={href} target="_blank" rel="noreferrer" data-testid="whatsapp-button"
                            className="flex items-center justify-center gap-2 py-3 rounded-full bg-[#25D366]/15 text-[#0a6a36] hover:bg-[#25D366]/25 text-xs font-medium tracking-[0.05em] uppercase">
                            <MessageCircle className="w-3.5 h-3.5" /> Chat on WhatsApp
                          </a>
                        );
                      })()}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 text-sm">
            <div className="p-4 rounded-2xl bg-[#F5F2EA]">
              <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547]">Format</div>
              <div className="mt-1 flex items-center gap-1">
                {isMedia ? "Stream / Listen" : product.is_digital ? <><Download className="w-4 h-4 text-[var(--accent)]" /> Instant digital</> : "Physical product"}
              </div>
            </div>
            <div className="p-4 rounded-2xl bg-[#F5F2EA]">
              <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547]">Delivery</div>
              <div className="mt-1" data-testid="delivery-text">{isExternal ? "Secure checkout & digital delivery via This That For Me Publishing Studio." : isPhysical ? "Ships from US" : isMedia ? "Embedded above" : "Email after payment"}</div>
            </div>
          </div>

          {product.tags?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {product.tags.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full bg-[#F5F2EA] text-xs text-[#5C5547]">#{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      <PaymentModal open={open} onOpenChange={setOpen} product={product} variant={selectedVariant}
        engraving={engravingGroup ? (selectedOptions[engravingGroup.name] || "") : ""} />
      <InquiryModal open={inquiryOpen} onOpenChange={setInquiryOpen} product={product} variant={selectedVariant} />
      <RelatedProducts productId={product.id} />
    </div>
  );
}
